import React, { Suspense, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { pdf } from '@react-pdf/renderer';
import { getFormConfig } from '../../forms';
import { useTenantTrades } from '../../hooks/useTenantTrades';
import { TRADE_META } from '../../utils/constants';
import FormDebug from '../common/FormDebug';
import LoadingSpinner from '../common/LoadingSpinner';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAlertTriangle, FiArrowLeft, FiShield } = FiIcons;

const FormRouter = () => {
  const { formId, formInstanceId } = useParams();
  const navigate = useNavigate();
  const { isFormAllowed, allowedCategories } = useTenantTrades();
  const [FormComponent, setFormComponent] = useState(null);
  const [PDFComponent, setPDFComponent] = useState(null);
  const [error, setError] = useState(null);
  const [tradeBlocked, setTradeBlocked] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  useEffect(() => {
    const loadForm = async () => {
      try {
        setLoading(true);
        setError(null);
        setTradeBlocked(null);

        console.log(`🔄 Loading form: ${formId}`);

        const formConfig = getFormConfig(formId);

        if (!formConfig) {
          throw new Error(`Form "${formId}" not found in registry`);
        }

        // Tenant Trade Gating: block direct URL access to certificates
        // outside the active organization's enabled trades.
        if (!isFormAllowed(formId)) {
          console.warn(`⛔ Trade gate blocked "${formId}" (${formConfig.category}) for active tenant`);
          setTradeBlocked({
            formName: formConfig.name,
            formCategory: formConfig.category,
            formIcon: formConfig.icon
          });
          setLoading(false);
          return;
        }

        if (!formConfig.component) {
          throw new Error(`Form "${formId}" has no component defined`);
        }
        
        // Load the form component
        let Component;
        if (typeof formConfig.component === 'function' && !formConfig.component.name) {
          const module = await formConfig.component();
          Component = module.default || module;
        } else {
          Component = formConfig.component;
        }
        
        if (!Component) {
          throw new Error(`Could not load component for form "${formId}"`);
        }
        
        // Load the PDF component if available
        let PDFComp = null;
        if (formConfig.pdfComponent) {
          if (typeof formConfig.pdfComponent === 'function' && !formConfig.pdfComponent.name) {
            const pdfModule = await formConfig.pdfComponent();
            PDFComp = pdfModule.default || pdfModule;
          } else {
            PDFComp = formConfig.pdfComponent;
          }
        }
        
        setFormComponent(() => Component);
        setPDFComponent(() => PDFComp);
        
      } catch (err) {
        console.error(`❌ Error loading form "${formId}":`, err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadForm();
  }, [formId, isFormAllowed]);

  const handleSave = (savedRecord) => {
    // Navigate to the instance URL if it's a new form
    if (!formInstanceId && savedRecord.id) {
        navigate(`/forms/${formId}/${savedRecord.id}`, { replace: true });
    }
  };

  const handleGeneratePDF = async (data) => {
    if (!PDFComponent) {
      console.error('PDF component not available for this form');
      alert('PDF generation not available for this form type');
      return;
    }

    try {
      setGeneratingPDF(true);
      console.log('📄 Generating PDF with data:', data);
      
      // Create the PDF document
      const PDFDoc = <PDFComponent formData={data} />;
      
      // Generate blob
      const blob = await pdf(PDFDoc).toBlob();
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const timestamp = new Date().toISOString().split('T')[0];
      const certificateNumber = data.certification?.certificateNumber || 'certificate';
      link.download = `${formId}-${certificateNumber}-${timestamp}.pdf`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup
      URL.revokeObjectURL(url);
      
      console.log('✅ PDF generated successfully');
      
    } catch (err) {
      console.error('❌ PDF generation failed:', err);
      alert(`Failed to generate PDF: ${err.message}`);
    } finally {
      setGeneratingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20">
        <LoadingSpinner message={`Loading ${formId} form...`} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center max-w-lg mx-auto">
          <SafeIcon icon={FiAlertTriangle} className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-red-700 mb-2">Error Loading Form</h2>
          <p className="text-red-600 mb-6">{error}</p>
          <div className="bg-white p-4 rounded text-left text-xs font-mono text-gray-600 mb-6 border border-gray-200">
             Form ID: {formId}
          </div>
          <button onClick={() => navigate('/forms')} className="btn btn-primary">
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
            Return to Form Library
          </button>
          <div className="mt-4 text-left">
            <FormDebug />
          </div>
        </div>
      </div>
    );
  }

  // Tenant Trade Gating: unauthorized trade banner for direct URL access
  if (tradeBlocked) {
    return (
      <div className="container py-12 px-4">
        <div className="max-w-lg mx-auto bg-white dark:bg-slate-900 border border-amber-200 dark:border-amber-900/60 rounded-2xl p-8 text-center shadow-xl">
          <div className="w-16 h-16 rounded-2xl bg-amber-50 dark:bg-amber-950/40 flex items-center justify-center mx-auto mb-5">
            <SafeIcon icon={FiShield} className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-lg font-black text-slate-900 dark:text-slate-100 mb-2">
            Access Restricted — Trade Not Enabled
          </h2>
          <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
            <span className="inline-flex items-center gap-1.5 font-bold text-slate-800 dark:text-slate-200">
              {tradeBlocked.formIcon} {tradeBlocked.formName}
            </span>{' '}
            belongs to the <strong>{tradeBlocked.formCategory}</strong> category, which is not
            enabled for your organization. Only an Owner or Admin can enable additional trades in
            Settings.
          </p>
          <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-left mb-6">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
              Your enabled trades
            </p>
            <div className="flex flex-wrap gap-1.5">
              {allowedCategories.map((cat) => (
                <span
                  key={cat}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300"
                >
                  {TRADE_META[cat]?.icon} {TRADE_META[cat]?.label || cat}
                </span>
              ))}
            </div>
          </div>
          <div className="flex gap-2.5">
            <button
              onClick={() => navigate('/settings')}
              className="flex-1 px-4 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-xl text-xs font-bold hover:bg-slate-50 dark:hover:bg-slate-800 transition-all"
            >
              Manage Trades in Settings
            </button>
            <button
              onClick={() => navigate('/forms')}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold shadow-md transition-all"
            >
              <SafeIcon icon={FiArrowLeft} className="w-3.5 h-3.5" />
              Back to Library
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={<LoadingSpinner message={`Loading ${formId} form...`} />}>
      {process.env.NODE_ENV === 'development' && <FormDebug />}
      {generatingPDF && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <LoadingSpinner message="Generating PDF..." />
          </div>
        </div>
      )}
      {FormComponent && (
        <FormComponent 
          formInstanceId={formInstanceId} 
          onSave={handleSave}
          onGeneratePDF={handleGeneratePDF}
        />
      )}
    </Suspense>
  );
};

export default FormRouter;
